import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { fetchProduct } from "../lib/cache_api";
import { useOnline } from "../lib/online";
import { enqueueAdjust } from "../lib/sync";
import Barcode from "react-barcode";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, Upload, Printer, Minus, Save, Copy } from "lucide-react";
import { fmtMoney } from "../lib/format";
import { toast } from "sonner";
import CategoryPicker from "../components/CategoryPicker";
import { readAndDownscaleImage, PHOTO_MAX_BYTES } from "../lib/image";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { online, refreshQueueCount } = useOnline();
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeVariant, setActiveVariant] = useState(0);
  const [duplicating, setDuplicating] = useState(false);
  const printRef = useRef(null);

  const load = async () => {
    const { data } = await fetchProduct(id);
    setProduct(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!product) return <div className="font-mono uppercase tracking-widest text-sm">Loading…</div>;

  const updateProduct = (patch) => setProduct((p) => ({ ...p, ...patch }));
  const updateVariant = (i, patch) => {
    setProduct((p) => ({
      ...p,
      variants: p.variants.map((v, idx) => idx === i ? { ...v, ...patch } : v)
    }));
  };

  const addAttr = (i) => {
    const key = prompt("Attribute name (e.g., Size, Color):");
    if (!key) return;
    updateVariant(i, { attributes: { ...product.variants[i].attributes, [key]: "" } });
  };
  const setAttrValue = (i, k, val) => {
    updateVariant(i, { attributes: { ...product.variants[i].attributes, [k]: val } });
  };
  const removeAttr = (i, k) => {
    const next = { ...product.variants[i].attributes };
    delete next[k];
    updateVariant(i, { attributes: next });
  };

  const addVariant = () => {
    setProduct((p) => ({
      ...p,
      variants: [...p.variants, { attributes: {}, stock: 0, price: 0 }]
    }));
  };
  const removeVariant = (i) => {
    setProduct((p) => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/products/${id}`, {
        name: product.name, description: product.description,
        categories: product.categories || [],
        photo_base64: product.photo_base64, variants: product.variants,
        low_stock_threshold: product.low_stock_threshold,
      });
      setProduct(data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const duplicate = async () => {
    if (duplicating) return;
    if (!window.confirm(`Duplicate "${product.name}"? A new product with " (Copy)" appended and fresh SKUs/barcodes will be created.`)) return;
    setDuplicating(true);
    try {
      const { data } = await api.post(`/products/${id}/duplicate`);
      toast.success("Product duplicated");
      navigate(`/products/${data.product_id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't duplicate");
    } finally { setDuplicating(false); }
  };

  const adjust = async (vIdx, delta) => {
    const v = product.variants[vIdx];
    if (!online) {
      const { product: updated } = await enqueueAdjust({
        product_id: id, variant_id: v.variant_id, delta,
      });
      if (updated) setProduct(updated);
      toast("Saved offline — will sync");
      refreshQueueCount();
      return;
    }
    try {
      const { data } = await api.post(`/products/${id}/variants/${v.variant_id}/adjust`, null, { params: { delta }});
      setProduct(data);
    } catch {
      const { product: updated } = await enqueueAdjust({
        product_id: id, variant_id: v.variant_id, delta,
      });
      if (updated) setProduct(updated);
      toast("Saved offline — will sync");
      refreshQueueCount();
    }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > PHOTO_MAX_BYTES) { toast.error("Max 8MB"); return; }
    updateProduct({ photo_base64: await readAndDownscaleImage(f) });
  };

  const printTag = () => {
    const node = printRef.current; if (!node) return;
    const w = window.open("", "", "width=420,height=640");
    if (!w) { toast.error("Pop-up blocked"); return; }
    // Build the doc with DOM APIs (no document.write / innerHTML string concat).
    const doc = w.document;
    doc.title = "Tag";
    const style = doc.createElement("style");
    style.textContent = "body{font-family:'Space Mono',monospace;margin:20px;color:#111}.tag{border:2px dashed #111;padding:20px;text-align:center}h1{font-family:sans-serif;margin:0 0 6px;font-weight:900;text-transform:uppercase}.sku{font-size:14px;margin-top:8px;letter-spacing:2px}";
    doc.head.appendChild(style);
    const wrap = doc.createElement("div");
    wrap.className = "tag";
    // Safely transfer the rendered React node into the print window.
    wrap.appendChild(node.cloneNode(true));
    doc.body.appendChild(wrap);
    w.focus(); w.print();
  };

  const v = product.variants[activeVariant] || product.variants[0];

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Entry · {product.base_sku}</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">{product.name}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={()=>navigate(-1)} className="brutal-btn brutal-btn-secondary">Back</button>
          {isAdmin && (
            <button onClick={duplicate} className="brutal-btn brutal-btn-secondary" disabled={duplicating} data-testid="duplicate-product-btn">
              <Copy size={14}/> <span className="ml-1">{duplicating ? "Duplicating…" : "Duplicate"}</span>
            </button>
          )}
          {isAdmin && (
            <button onClick={save} className="brutal-btn" disabled={saving} data-testid="save-product-btn">
              <Save size={14}/> {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="brutal-card p-6 lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="brutal-label">Name</label>
              <input className="brutal-input" value={product.name} disabled={!isAdmin} onChange={(e)=>updateProduct({ name: e.target.value })}/>
            </div>
            <div>
              <label className="brutal-label">Categories</label>
              <CategoryPicker
                value={product.categories || []}
                onChange={(cats)=>updateProduct({ categories: cats })}
                disabled={!isAdmin}
                testid="product-categories"
              />
            </div>
          </div>
          <div>
            <label className="brutal-label">Description</label>
            <textarea rows={3} className="brutal-input" value={product.description||""} disabled={!isAdmin} onChange={(e)=>updateProduct({ description: e.target.value })}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="brutal-label">Threshold</label>
              <input type="number" className="brutal-input" value={product.low_stock_threshold} disabled={!isAdmin} onChange={(e)=>updateProduct({ low_stock_threshold: Number(e.target.value) })}/>
            </div>
          </div>
        </div>

        <div className="brutal-card p-6">
          <label className="brutal-label">Photo</label>
          {product.photo_base64 ? (
            <img src={product.photo_base64} alt="" className="w-full aspect-square object-cover border-2 border-black"/>
          ) : (
            <label className="cursor-pointer border-2 border-dashed border-black aspect-square flex items-center justify-center bg-[#F9F8F6]">
              <div className="text-center">
                <Upload size={22} className="mx-auto"/>
                <div className="mt-2 font-mono text-xs uppercase tracking-widest">Upload</div>
              </div>
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden"/>
            </label>
          )}
        </div>
      </div>

      {/* Variants */}
      <div className="brutal-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-black uppercase">Variants</h2>
          <button className="brutal-btn brutal-btn-secondary text-xs" onClick={addVariant} data-testid="add-variant-btn">
            <Plus size={12}/> Add
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {product.variants.map((vv, i) => (
            <button key={vv.variant_id || i}
              onClick={()=>setActiveVariant(i)}
              className={`brutal-badge cursor-pointer ${activeVariant===i ? "!bg-[#111] !text-white" : ""}`}
              data-testid={`variant-tab-${i}`}>
              #{i+1} · {vv.sku || "NEW"}
            </button>
          ))}
        </div>

        {v && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="brutal-label">Attributes</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(v.attributes || {}).map(([k, val]) => (
                    <div key={k} className="brutal-badge !py-1 flex items-center gap-2">
                      <span>{k}:</span>
                      <input className="font-mono text-xs bg-transparent outline-none w-20" value={val} disabled={!isAdmin}
                        onChange={(e)=>setAttrValue(activeVariant, k, e.target.value)}/>
                      {isAdmin && <button onClick={()=>removeAttr(activeVariant, k)} className="text-[#D9381E]">×</button>}
                    </div>
                  ))}
                  {isAdmin && <button className="brutal-badge" onClick={()=>addAttr(activeVariant)}>+ Attr</button>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="brutal-label">Stock</label>
                  <div className="flex items-center gap-2">
                    <button onClick={()=>adjust(activeVariant, -1)} className="brutal-btn brutal-btn-secondary !p-2 min-w-[44px] min-h-[44px] flex-shrink-0" data-testid="stock-minus"><Minus size={14}/></button>
                    <input type="number" inputMode="numeric" className="brutal-input text-center min-w-0" value={v.stock} disabled={!isAdmin}
                      onChange={(e)=>updateVariant(activeVariant, { stock: Number(e.target.value) })}/>
                    <button onClick={()=>adjust(activeVariant, 1)} className="brutal-btn brutal-btn-secondary !p-2 min-w-[44px] min-h-[44px] flex-shrink-0" data-testid="stock-plus"><Plus size={14}/></button>
                  </div>
                </div>
                <div>
                  <label className="brutal-label">Cost</label>
                  <input type="number" step="0.01" inputMode="decimal" className="brutal-input" value={v.cost ?? 0} disabled={!isAdmin}
                    onChange={(e)=>updateVariant(activeVariant, { cost: Number(e.target.value) })} placeholder="0.00"/>
                </div>
                <div>
                  <label className="brutal-label">Price</label>
                  <input type="number" step="0.01" inputMode="decimal" className="brutal-input" value={v.price} disabled={!isAdmin}
                    onChange={(e)=>updateVariant(activeVariant, { price: Number(e.target.value) })}/>
                </div>
              </div>

              {isAdmin && product.variants.length > 1 && (
                <button className="brutal-btn brutal-btn-danger text-xs" onClick={()=>removeVariant(activeVariant)}>
                  <Trash2 size={12}/> Remove Variant
                </button>
              )}
            </div>

            {/* Shipping Tag with Barcode */}
            <div className="shipping-tag" data-testid="barcode-tag">
              <div ref={printRef}>
                <h1 className="font-heading uppercase text-xl text-center">{product.name}</h1>
                <div className="text-center font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                  {Object.entries(v.attributes || {}).map(([k,val]) => `${k}: ${val}`).join(" · ") || "Default"}
                </div>
                {v.barcode ? (
                  <div className="bg-white p-3 border-2 border-black mt-4 flex justify-center">
                    <Barcode value={v.barcode} height={60} width={1.6} fontSize={12} background="#fff" lineColor="#111" />
                  </div>
                ) : (
                  <div className="bg-white p-3 border-2 border-black mt-4 text-center font-mono text-xs">Save to generate</div>
                )}
                <div className="sku font-mono text-center mt-3">
                  SKU: {v.sku || "—"}
                </div>
                {v.price ? <div className="text-center font-heading font-black text-2xl mt-2">{fmtMoney(v.price)}</div> : null}
              </div>
              <button onClick={printTag} className="brutal-btn w-full mt-4" data-testid="print-tag-btn">
                <Printer size={14}/> Print Tag
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
